import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Footer } from '../components/Footer';
import { Navigation } from '../components/Navigation';
import { highlightJSON } from '../utils/highlightJSON';
const pageData = {
  $schema: 'https://schema.org/AboutPage',
  name: 'Andoe / About',
  frontmatter: {
    page: 'About',
    topic: 'Company',
    status: 'Active'
  },
  hero: {
    kicker: 'Andoe / About',
    headline: 'A team of AI marketing agents, on retainer.',
    lede: 'We built Andoe so small teams can compete with enterprises, without the enterprise headcount, budget, or tooling stack.'
  },
  sections: [{
    ref: '01',
    title: 'What we do',
    deck: 'AEO, GEO, and SEO, run by a team of agents.',
    entries: [{
      ref: '1.1',
      label: 'Scope',
      headline: "AI search isn't replacing SEO. It's expanding on top of it.",
      body: 'Discovery now happens across search engines, answer engines, and generative models. Andoe brings AEO, GEO, and SEO together in one platform, run by a team of agents working on your behalf.'
    }, {
      ref: '1.2',
      label: 'Approach',
      headline: "We're not an analytics platform.",
      body: "Analytics tools hand you dashboards and leave the thinking to you. Andoe's agents capture the data, analyze it without hallucination, and deliver weekly reports with clear insights and next steps. You spend your time acting, not interpreting."
    }]
  }, {
    ref: '02',
    title: 'How we think',
    deck: 'A few principles that shape the product.',
    entries: [{
      ref: '2.1',
      label: 'Coverage',
      headline: 'Track the two LLMs that matter, not all of them.',
      body: "Optimize for the majors and you're covered across the rest. Paying to monitor the long tail is a tax, not a strategy."
    }, {
      ref: '2.2',
      label: 'Signal',
      headline: "More prompts isn't more clarity.",
      body: "If you can't review each response, the data is noise. We focus on the prompts that actually move outcomes."
    }, {
      ref: '2.3',
      label: 'Cadence',
      headline: 'Weekly beats daily.',
      body: 'LLMs are probabilistic, but they stay within contextual semantics. Daily tracking creates anxiety, not insight. Review weekly, then spend the rest of the week taking action. Andoe handles the analysis.'
    }, {
      ref: '2.4',
      label: 'Authorship',
      headline: "Don't fully automate content with AI.",
      body: 'Models are very good at recognizing model-generated text, and LLM providers have no incentive to reward it. They will always champion authentic content. Andoe gives you a step-by-step playbook with starter prompts so the work stays yours, just faster.'
    }]
  }],
  end_cta: {
    label: '03',
    headline: 'Build Andoe with us.',
    body: "We're early, and the best products are built with real feedback from real users. We're looking for teams who want to shape Andoe alongside us, not just use it, but help define what it becomes.",
    cta: {
      label: 'Get in touch',
      href: '/contact'
    }
  }
};
export function AboutPage() {
  const [jsonHtml, setJsonHtml] = useState('');
  useEffect(() => {
    setJsonHtml('<span class="c">// about andoe, as structured data</span>\n\n' + highlightJSON(pageData));
  }, []);
  return <>
      <Navigation data-id="element-56" />

      <main className="wrap report-main" data-id="element-57">
        <article className="report" data-id="element-58">
          {/* frontmatter */}
          <header className="frontmatter" data-id="element-59">
            <div className="fm-cell" data-id="element-60">
              <span className="lbl" data-id="element-61">Page</span>
              <span className="val" data-id="element-62">About</span>
            </div>
            <div className="fm-cell" data-id="element-63">
              <span className="lbl" data-id="element-64">Topic</span>
              <span className="val" data-id="element-65">Company</span>
            </div>
            <div className="fm-cell" data-id="element-66">
              <span className="lbl" data-id="element-67">Status</span>
              <span className="val" data-id="element-68">Active</span>
            </div>
          </header>

          {/* HERO */}
          <section className="hero" data-id="element-69">
            <div className="kicker" data-id="element-70">Andoe / About</div>
            <h1 data-id="element-71">A team of AI marketing agents, on retainer.</h1>
            <p className="lede" data-id="element-72">
              We built Andoe so small teams can compete with enterprises,
              without the enterprise headcount, budget, or tooling stack.
            </p>
          </section>

          {/* 01 WHAT WE DO */}
          <section className="report-section" data-id="element-73">
            <div className="section-head" data-id="element-74">
              <div className="section-num" data-id="element-75">01</div>
              <div className="section-title" data-id="element-76">
                <h2 data-id="element-77">What we do</h2>
                <span className="deck" data-id="element-78">
                  AEO, GEO, and SEO, run by a team of agents.
                </span>
              </div>
            </div>
            <div className="section-body" data-id="element-79">
              <div className="pad" data-id="element-80"></div>
              <div className="content" data-id="element-81">
                <div className="entry" data-id="element-82">
                  <div className="entry-label" data-id="element-83">
                    <span className="num" data-id="element-84">1.1</span> Scope
                  </div>
                  <h3 data-id="element-85">
                    AI search isn't replacing SEO. It's expanding on top of it.
                  </h3>
                  <p data-id="element-86">
                    Discovery now happens across search engines, answer engines,
                    and generative models. Andoe brings{' '}
                    <strong data-id="element-87">AEO, GEO, and SEO</strong> together in one platform,
                    run by a team of agents working on your behalf.
                  </p>
                </div>
                <div className="entry" data-id="element-88">
                  <div className="entry-label" data-id="element-89">
                    <span className="num" data-id="element-90">1.2</span> Approach
                  </div>
                  <h3 data-id="element-91">We're not an analytics platform.</h3>
                  <p data-id="element-92">
                    Analytics tools hand you dashboards and leave the thinking
                    to you. Andoe's agents capture the data, analyze it without
                    hallucination, and deliver weekly reports with clear
                    insights and next steps. You spend your time{' '}
                    <strong data-id="element-93">acting, not interpreting</strong>.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* 02 HOW WE THINK */}
          <section className="report-section" data-id="element-94">
            <div className="section-head" data-id="element-95">
              <div className="section-num" data-id="element-96">02</div>
              <div className="section-title" data-id="element-97">
                <h2 data-id="element-98">How we think</h2>
                <span className="deck" data-id="element-99">
                  A few principles that shape the product.
                </span>
              </div>
            </div>
            <div className="section-body" data-id="element-100">
              <div className="pad" data-id="element-101"></div>
              <div className="content" data-id="element-102">
                <div className="entry" data-id="element-103">
                  <div className="entry-label" data-id="element-104">
                    <span className="num" data-id="element-105">2.1</span> Coverage
                  </div>
                  <h3 data-id="element-106">Track the two LLMs that matter, not all of them.</h3>
                  <p data-id="element-107">
                    Optimize for the majors and you're{' '}
                    <strong data-id="element-108">covered across the rest</strong>. Paying to monitor
                    the long tail is a tax, not a strategy.
                  </p>
                </div>
                <div className="entry" data-id="element-109">
                  <div className="entry-label" data-id="element-110">
                    <span className="num" data-id="element-111">2.2</span> Signal
                  </div>
                  <h3 data-id="element-112">More prompts isn't more clarity.</h3>
                  <p data-id="element-113">
                    If you can't review each response, the data is noise. We
                    focus on the prompts that actually move outcomes.
                  </p>
                </div>
                <div className="entry" data-id="element-114">
                  <div className="entry-label" data-id="element-115">
                    <span className="num" data-id="element-116">2.3</span> Cadence
                  </div>
                  <h3 data-id="element-117">Weekly beats daily.</h3>
                  <p data-id="element-118">
                    LLMs are probabilistic, but they stay within contextual
                    semantics. Even though a model samples from a distribution
                    of possible next tokens, it doesn't drift into nonsense or
                    unrelated topics when you are tracking specific prompts. So
                    daily tracking becomes a cost center. Review weekly, then
                    spend the rest of the week taking action. Andoe handles the
                    analysis.
                  </p>
                </div>
                <div className="entry" data-id="element-119">
                  <div className="entry-label" data-id="element-120">
                    <span className="num" data-id="element-121">2.4</span> Authorship
                  </div>
                  <h3 data-id="element-122">Don't fully automate content with AI.</h3>
                  <p data-id="element-123">
                    Models are very good at recognizing model-generated text,
                    and LLM providers have no incentive to reward it. They will
                    always champion <strong data-id="element-124">authentic content</strong>. Andoe
                    gives you a step-by-step playbook with starter prompts so
                    the work stays yours, just faster.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* END CTA / 03 */}
          <section className="end-cta" id="start" data-id="element-125">
            <div className="end-label" data-id="element-126">03</div>
            <h2 data-id="element-127">Build Andoe with us.</h2>
            <p data-id="element-128">
              We're early, and the best products are built with real feedback
              from real users. We're looking for teams who want to shape Andoe
              alongside us, not just use it, but help define what it becomes.
            </p>
            <Link to="/contact" className="cta" data-id="element-129">
              Get in touch <span className="arrow" data-id="element-130">→</span>
            </Link>
          </section>

          <Footer data-id="element-131" />
        </article>

        <pre className="json-view" dangerouslySetInnerHTML={{
        __html: jsonHtml
      }} data-id="element-132" />
      </main>
    </>;
}